import { Download, Loader2, MoreVertical, Timer, Users, WalletCards } from 'lucide-react'

import BarChart from '../components/dashboard/BarChart'
import DashboardMetric from '../components/dashboard/DashboardMetric'
import DashboardPeriodFilter from '../components/dashboard/DashboardPeriodFilter'
import DonutChart from '../components/dashboard/DonutChart'
import RevenueSplitCard from '../components/dashboard/RevenueSplitCard'
import TimeRow from '../components/dashboard/TimeRow'
import TopProcedure from '../components/dashboard/TopProcedure'
import { formatMoney, numberFormatter } from '../utils/formatters'

export default function DashboardPage({
  dashboard,
  dashboardPeriod,
  isLoading,
  onPeriodChange,
  revenueByType,
  topProcedureMax,
}) {
  if (isLoading && !dashboard) {
    return (
      <div className="flex min-h-[320px] items-center justify-center rounded-xl bg-white text-sm font-semibold text-[#5A5147] shadow-[0_18px_45px_rgba(26,26,26,0.05)]">
        <Loader2 className="mr-3 animate-spin" size={22} />
        Carregando dashboard
      </div>
    )
  }

  const totalPatients = dashboard?.total_patients || 0
  const topProcedures = dashboard?.top_procedures || []
  const proceduresForChart = topProcedures.length
    ? topProcedures
    : [
        { procedure: 'Implantes', revenue: 0, count: 0 },
        { procedure: 'Ortodontia', revenue: 0, count: 0 },
        { procedure: 'Prótese', revenue: 0, count: 0 },
        { procedure: 'Estética', revenue: 0, count: 0 },
        { procedure: 'Clínico Geral', revenue: 0, count: 0 },
      ]
  const proceduresByClinicalTime = [...proceduresForChart].sort(
    (a, b) => (b.total_duration_minutes || 0) - (a.total_duration_minutes || 0),
  )
  const clinicalTimeMax = Math.max(
    ...proceduresByClinicalTime.map((item) => item.total_duration_minutes || 0),
    1,
  )

  return (
    <div>
      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-normal text-[#1A1A1A] lg:text-3xl">
            Dashboard de Faturamento
          </h1>
          <p className="mt-2 text-sm font-medium text-[#5A5147] lg:text-base">
            Visão geral financeira e de performance clínica.
          </p>
        </div>

        <DashboardPeriodFilter value={dashboardPeriod} onChange={onPeriodChange} />
      </div>

      <section className="mb-5 grid gap-3 sm:gap-4 xl:grid-cols-3">
        <DashboardMetric
          icon={WalletCards}
          label="Faturamento total"
          note="+12% em relação ao mês anterior"
          tone="green"
          trend="up"
          value={formatMoney(dashboard?.total_revenue || 0)}
        />
        <DashboardMetric
          icon={Users}
          label="Total de atendimentos"
          note={`${Math.round((dashboard?.total_duration_minutes || 0) / 60)} horas clínicas`}
          tone="pink"
          value={numberFormatter.format(totalPatients)}
        />
        <DashboardMetric
          icon={Download}
          label="Ticket médio"
          note="-2% em relação ao mês anterior"
          tone="gold"
          trend="down"
          value={formatMoney(dashboard?.average_ticket || 0)}
        />
      </section>

      <section className="grid gap-3 sm:gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="grid gap-3 sm:gap-4">
          <div className="rounded-xl bg-white p-3 shadow-[0_18px_45px_rgba(26,26,26,0.05)] sm:p-4">
            <div className="mb-3 flex items-start justify-between sm:mb-4">
              <h2 className="text-base font-bold text-[#1A1A1A] sm:text-lg">
                Faturamento: Particular vs Convênio
              </h2>
              <MoreVertical className="text-[#BCAFA0]" size={18} sm:size={20} />
            </div>

            <div className="grid items-center gap-3 lg:grid-cols-[160px_minmax(0,1fr)] sm:gap-4">
              <DonutChart percent={revenueByType.particularPercent} />

              <div className="grid gap-2 sm:gap-3">
                <RevenueSplitCard
                  active
                  count={revenueByType.particular.count}
                  label="Particular"
                  value={revenueByType.particular.revenue}
                />
                <RevenueSplitCard
                  count={revenueByType.convenio.count}
                  label="Convênio"
                  value={revenueByType.convenio.revenue}
                />
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-white p-3 shadow-[0_18px_45px_rgba(26,26,26,0.05)] sm:p-4">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:mb-4 sm:gap-3">
              <h2 className="text-base font-bold text-[#1A1A1A] sm:text-lg">
                Faturamento por Tipo de Procedimento
              </h2>
            </div>

            <BarChart procedures={proceduresForChart} max={topProcedureMax} />
          </div>
        </div>

        <aside className="grid gap-3 sm:gap-4">
          <div className="rounded-xl bg-white p-3 shadow-[0_18px_45px_rgba(26,26,26,0.05)] sm:p-4">
            <h2 className="mb-3 text-base font-bold text-[#1A1A1A] sm:mb-4 sm:text-lg">Top Procedimentos</h2>
            <div className="grid gap-2 sm:gap-3">
              {proceduresForChart.slice(0, 4).map((item, index) => (
                <TopProcedure key={`${item.procedure}-${index}`} item={item} index={index} />
              ))}
            </div>
          </div>

          <div className="rounded-xl bg-white p-3 text-[#1A1A1A] shadow-[0_18px_45px_rgba(26,26,26,0.05)] sm:p-4">
            <div className="mb-3 flex items-start justify-between sm:mb-4">
              <h2 className="max-w-[220px] text-base font-bold leading-tight sm:text-lg">
                Tempo Clínico Gasto
              </h2>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F8E1E7] text-[#7A5963] sm:h-9 sm:w-9">
                <Timer size={18} strokeWidth={2.3} sm:size={20} />
              </div>
            </div>
            <div className="grid gap-3 sm:gap-4">
              {proceduresByClinicalTime.slice(0, 4).map((item) => {
                const minutes = item.total_duration_minutes || 0
                return (
                  <TimeRow
                    key={`${item.procedure}-time`}
                    label={item.procedure}
                    minutes={minutes}
                    percent={minutes ? Math.max(8, (minutes / clinicalTimeMax) * 100) : 0}
                  />
                )
              })}
            </div>
          </div>
        </aside>
      </section>
    </div>
  )
}

